// セクションカード(アコーディオン + 状態バッジ)共通部品。

import { useState, ReactNode } from "react";
import { Paper, Box, Typography, Chip, Collapse, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ErrorIcon from "@mui/icons-material/Error";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// ===== ステップ状態(未完了/進行中/完了) =====
export type StepStatus = "incomplete" | "in_progress" | "done";

export const STEP_STATUS_META = {
  incomplete: { color: "warning" as const, label: "未完了", icon: <ErrorIcon /> },
  in_progress: { color: "info" as const, label: "進行中", icon: <PlayArrowIcon /> },
  done: { color: "success" as const, label: "完了", icon: <CheckCircleIcon /> },
};

// 未完了時のみ不足項目リストを表示対象にする
function errorListOf(isDone: boolean, errors?: string[]): string[] | null {
  if (!isDone && errors != null && errors.length > 0) {
    return errors;
  }
  return null;
}

// セクションカード(アコーディオン + 状態バッジ)。未完了時は info の下に不足項目を列挙する。
export function SectionCard({ title, status, info, errors, children }: {
  title: string;
  status: StepStatus;
  info: ReactNode;
  errors?: string[];
  children: ReactNode;
}) {
  const meta = STEP_STATUS_META[status];
  const isDone = status === "done";
  const errorList = errorListOf(isDone, errors);
  // 既定は常に展開。完了済みセクションも内容を確認できるようにする(折りたたみは手動操作のみ)
  const [expanded, setExpanded] = useState(true);
  return (
    <Paper variant="outlined" sx={{ borderColor: status === "incomplete" ? "warning.main" : "divider" }}>
      <Box onClick={() => setExpanded((v) => !v)}
        sx={{ px: 2, pt: 1.5, pb: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", cursor: "pointer" }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        <Chip size="small" color={meta.color} variant={isDone ? "filled" : "outlined"} icon={meta.icon} label={meta.label} />
        <Box sx={{ flex: 1 }} />
        {expanded ? <KeyboardArrowUpIcon color="action" /> : <KeyboardArrowDownIcon color="action" />}
      </Box>
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity={meta.color} sx={{ py: 0.25, mb: 1.5 }}>
            {info}
            {errorList != null && (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {errorList?.map((e, i) => <li key={i}><Typography variant="caption">{e}</Typography></li>)}
              </Box>
            )}
          </Alert>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
}
